import { useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal, { Actions } from 'components/shared/StandardModal';
import ThinSearch from 'components/shared/ThinSearch';
import Workflow, { WorkflowGroupOptions } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { FONT_WEIGHT_MEDIUM } from 'styles/theme/textStyles';
import { BodySmall, BodySmallBold } from 'styles/typography';
import NetworkProviderIcon from 'svg/NetworkProviderIcon';
import PencilIcon from 'svg/PencilIcon';

import { sortedGroupsByName } from './FanoutPanel';
import ReferralModal from './ReferralModal';

// "SNF" → "SNFs", "Skilled Nursing Facility" → "Skilled Nursing Facilities", "Agency" → "Agencies".
function pluralize(label: string): string {
  if (/[^aeiou]y$/i.test(label)) return `${label.slice(0, -1)}ies`;
  if (/(s|x|ch|sh)$/i.test(label)) return `${label}es`;
  return `${label}s`;
}

// The count summary reads by group type when the buildings agree on one ("3 SNFs"),
// falling back to the neutral noun for mixed or untyped groups. The Set includes
// undefined so "2 SNFs + 1 untyped" reads mixed, not uniformly typed.
//
// Typed to the one field it actually reads rather than to WorkflowGroupOptions, so the
// duplicate-review card can share this rule (OLIO-3382). Its candidates carry facilities off a
// stored search result, not full group records, and a second copy of the plural here is how one
// surface ends up calling an agency a SNF.
export function sentToSummary(groups: { groupType?: { displayName?: string } | null }[]): string {
  const single = groups.length === 1;
  const types = new Set(groups.map((group) => group.groupType?.displayName));
  const shared = types.size === 1 ? [...types][0] : undefined;
  if (shared) return `${groups.length} ${single ? shared : pluralize(shared)}`;
  return `${groups.length} ${single ? 'Facility' : 'Facilities'}`;
}

// What the modal's count says. Searching narrows the rows, so the count has to narrow with
// them — "3 of 94 SNFs" — instead of contradicting what is on screen (QA, OLIO-2734). The
// label keeps coming from the whole set so the type and its plural don't shift while typing.
function filteredSummary(groups: WorkflowGroupOptions[], visibleCount: number): string {
  const all = sentToSummary(groups);
  return visibleCount === groups.length ? all : `${visibleCount} of ${all}`;
}

// The "Referral Sent To" rail card (OLIO-2734, replacing the header chips): one
// building shows its name inline; several collapse to a count with a View link
// that opens the searchable list modal.
// `editable` adds the Edit link (UX, 2026-08-05) — with the edit modals split by what they edit,
// this card is where the buildings themselves are changed. The caller decides, so the link appears
// on exactly the referrals whose three-dot menu does.
export default function ReferralSentTo({ workflow, editable = false }: { workflow: Workflow; editable?: boolean }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const groups = workflow.groups;
  if (groups.length === 0) return null;

  return (
    <Card data-testid='referral-sent-to'>
      <Header>
        <BodySmallBold>Referral Sent To</BodySmallBold>
        {editable && (
          <EditLink type='button' data-testid='edit-facilities' onClick={() => setEditing(true)}>
            <PencilIcon color={colors.blue100} width={12} height={12} />
            Edit
          </EditLink>
        )}
      </Header>
      <Row>
        <NetworkProviderIcon width={13} height={13} />
        {groups.length === 1 ? (
          <BodySmall>{groups[0].name}</BodySmall>
        ) : (
          <BodySmall>
            {sentToSummary(groups)} &middot;{' '}
            <ViewLink type='button' data-testid='sent-to-view' onClick={() => setOpen(true)}>
              View
            </ViewLink>
          </BodySmall>
        )}
      </Row>
      {open && <SentToModal groups={groups} onClose={() => setOpen(false)} />}
      {editing && <ReferralModal workflow={workflow} section='facilities' onClose={() => setEditing(false)} />}
    </Card>
  );
}

function SentToModal({ groups, onClose }: { groups: WorkflowGroupOptions[]; onClose: () => void }) {
  const [search, setSearch] = useState('');
  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matched = query ? groups.filter((group) => group.name.toLowerCase().includes(query)) : groups;
    // The shared comparator, so this list can never disagree with the outcome
    // panel / facility dropdown ordering.
    return sortedGroupsByName(matched);
  }, [groups, search]);

  return (
    <StandardModal title='Referral Sent To' onCancel={onClose}>
      <ModalBody data-testid='sent-to-modal'>
        <Count>{filteredSummary(groups, visible.length)}</Count>
        <ThinSearch value={search} onChange={setSearch} placeholder='Search' />
        <List>
          {visible.map((group) => (
            <ListRow key={group.id} data-testid='sent-to-row'>
              {group.name}
            </ListRow>
          ))}
          {visible.length === 0 && <Empty>No facilities match your search.</Empty>}
        </List>
        <Actions>
          <Button onClick={onClose}>Close</Button>
        </Actions>
      </ModalBody>
    </StandardModal>
  );
}

const Card = styled.div`
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  background: ${colors.white};
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

// Matches the discharge planning card's "Update" link (patient story page): the theme's label
// size/weight, bold, rather than `font: inherit` — inheriting the card's base body size made the
// link read as loud as the card's own heading.
export const EditLink = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: ${({ theme }) => theme.textStyles.label.fontSize};
  line-height: ${({ theme }) => theme.textStyles.label.lineHeight};
  font-weight: ${FONT_WEIGHT_MEDIUM};
  color: ${colors.blue100};
  cursor: pointer;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
`;

const ViewLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: ${colors.blue100};
  text-decoration: underline;
  cursor: pointer;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Count = styled(BodySmallBold)`
  text-align: center;
`;

const List = styled.div`
  max-height: 320px;
  overflow-y: auto;
`;

const ListRow = styled(BodySmall)`
  display: block;
  padding: 12px 4px;
  border-bottom: 1px solid ${colors.black10};

  &:last-child {
    border-bottom: none;
  }
`;

const Empty = styled(BodySmall)`
  display: block;
  padding: 12px 4px;
  color: ${colors.black50};
`;
